"use client";

import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";
import { IUser } from "../types/user.types";
import { useRouter } from "next/navigation";
import { AuthContextType } from "../types/types";
import { removeCacheData } from "../lib/cacheService";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<IUser | null>(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();

  const openAuthModal = () => setIsAuthModalOpen(true);
  const closeAuthModal = () => setIsAuthModalOpen(false);

  const openSettingsModal = () => setIsSettingsModalOpen(true);
  const closeSettingsModal = () => setIsSettingsModalOpen(false);

  const logout = useCallback(async () => {
    try {
      await axios.post("/api/user/logout");
      removeCacheData();
    } catch (error) {
      console.error("Logout failed on server:", error);
    } finally {
      setUser(null);
      router.push("/");
    }
  }, [router]);

  const isWholesaleUser = useMemo(() => {
    if (process.env.NEXT_PUBLIC_ENABLE_WHOLESALE !== 'true') return false;
    return user?.role === "wholesale";
  }, [user]);

  useEffect(() => {
    const checkUserSession = async () => {
      try {
        const { data } = await axios.get("/api/user/me");
        if (data.success && data.user) {
          setUser(data.user);
        }
      } catch (error) {
        console.error(error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    checkUserSession();
  }, []);

  const value = useMemo(
    () => ({
      user,
      setUser,
      isLoading,
      isAuthModalOpen,
      openAuthModal,
      closeAuthModal,
      isSettingsModalOpen,
      openSettingsModal,
      closeSettingsModal,
      isWholesaleUser,
      logout,
    }),
    [
      user,
      isLoading,
      isAuthModalOpen,
      isWholesaleUser,
      isSettingsModalOpen,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
